import { cn, pubsub } from "utils";

import React from "react";
import { OspToast } from "@ospweb/sdk-ui";
import { TokenCardData } from "./types";
import { I18nUtil } from "@utils/i18n";
import { NextImage } from "@/src/components/NextImage";
import Translations from "@translate/components/Chat/OthersReportPanel/OthersReportPanelWithData";
import { pubSubKeyMap } from "@/src/utils/pubSubKeyMap";
import { useAggregatedReport360 } from "@/src/hooks/useAggregatedReport360";

const I18n = I18nUtil.createScoped(Translations);

export interface OthersReportPanelWithDataProps {
  title?: string;
  className?: string;
  limit?: number;
  cardType: string;
  themes?: string[];
  enabled?: boolean;
  id?: string;
  market?: string;
}

/**
 * 带数据获取功能的 OthersReportPanel 组件
 *
 * 使用 useAggregatedReport360 根据 cardType 获取对应的数据
 * 支持 stock、token、pool 类型的数据获取
 */
export const OthersReportPanelWithData: React.FC<
  OthersReportPanelWithDataProps
> = ({
  title = I18n.t("Others 360° Report"),
  className = "",
  limit = 5,
  cardType,
  themes,
  enabled,
  id,
  market,
}) => {
  // 获取聚合的360报告数据
  const {
    data: tokens,
    isLoading,
    error,
  } = useAggregatedReport360({
    cardType,
    themes,
    id,
    market,
  });

  if (tokens.length < 2) {
    return null;
  }
  // const handleTokenClick = (tokenId: string) => {
  //   log.debug(`[OthersReportPanelWithData] Clicked token: ${tokenId}`);
  //   onTokenClick?.(tokenId);
  // };
  const handleTokenClick = (token: TokenCardData) => {
    if (!enabled) {
      OspToast.info({
        content: I18n.t("Please retry after the current response completes"),
      });
      return;
    }
    pubsub.emit(pubSubKeyMap.CHAT_360, {
      id: token?.id || "",
      type: cardType === "token" ? "crypto" : "stock",
      symbol: token.symbol || "",
    });
  };

  // 显示加载状态
  if (isLoading) {
    return (
      <div className={cn("flex flex-col gap-3", className)}>
        <p
          className={cn("w-fit title_m text-content_primary whitespace-nowrap")}
        >
          <span className={cn("body_medium_m text-content_primary")}>
            {title}
          </span>
        </p>
        <div className={cn("flex flex-wrap gap-2 w-full")}>
          {/* 加载占位符 */}
          {Array.from({ length: limit }).map((_, index) => (
            <div
              key={`loading-${index}`}
              className={cn(
                "inline-flex items-center gap-1",
                "px-3 py-2 pl-2",
                "flex-shrink-0",
                "bg-background_white_overlay5",
                "rounded-2",
                "animate-pulse",
                "h-10 w-20",
              )}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {/* Title */}
      <p className={cn("w-fit title_m text-content_primary whitespace-nowrap")}>
        <span className={cn("body_medium_m text-content_primary")}>
          {title}
        </span>
      </p>
      {/* Error state */}
      {error && (
        <div className={cn("text-sm text-red-500 mb-2")}>
          {I18n.t("Failed to load data")}
        </div>
      )}
      {/* Token Cards Grid */}
      <div className={cn("flex flex-wrap gap-2 w-full")}>
        {tokens.map((token) => (
          <div
            data-openreplay-label="OthersReportPanel · OthersReportPanelWithData · div"
            key={token.id}
            className={cn(
              "inline-flex items-center gap-1",
              "px-3 py-2 pl-2",
              "flex-shrink-0",
              "bg-background_white_overlay5",
              "rounded-2",
              "cursor-pointer",
              "transition-all duration-200",
              "hover:opacity-80",
              token.className,
            )}
            onClick={() => handleTokenClick(token)}
          >
            {/* Card Header */}
            <div
              className={cn(
                "inline-flex items-center gap-2",
                "rounded-4",
                "flex-shrink-0",
              )}
            >
              {/* Avatar */}
              <div
                className={cn(
                  "w-6 h-6",
                  "rounded-full",
                  "border border-border_solid",
                  "aspect-ratio-1",
                  "overflow-hidden",
                  "flex items-center justify-center",
                )}
              >
                <NextImage
                  src={token.icon}
                  alt={`${token.name} icon`}
                  width={24}
                  height={24}
                  className={cn("w-full h-full object-cover")}
                />
              </div>

              {/* Card Label */}
              <div
                className={cn(
                  "inline-flex items-center gap-2.5",
                  "flex-shrink-0",
                )}
              >
                <div
                  className={cn(
                    "w-fit",
                    "body_medium_m",
                    "text-content_primary",
                    "whitespace-nowrap",
                  )}
                >
                  {token.name}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
